// src/components/ActivityLogPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHistory,
    faFilter,
    faPlus,
    faPencilAlt,
    faTrashAlt,
    faUsers,
    faUser,
    faUserPlus,
    faCalendarAlt,
    faRepeat,
    faLayerGroup,
    faUserCheck,
    faBan,
    faSpinner,
    faSearch,
    faTimes,
    faChevronLeft,
    faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { db } from '../firebase';
import { collection, query, where, orderBy, getDocs, limit, startAfter, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow, format, isThisYear, isToday, isYesterday } from 'date-fns';

import './ActivityLogPage.css';

const PAGE_SIZE = 25;

// Format timestamp for display
const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);

    if (isToday(date)) {
        return `Today at ${format(date, 'h:mm a')}`;
    }
    if (isYesterday(date)) {
        return `Yesterday at ${format(date, 'h:mm a')}`;
    }
    if (isThisYear(date)) {
        return format(date, 'MMM d \'at\' h:mm a');
    }
    return format(date, 'MMM d, yyyy \'at\' h:mm a');
};

// Format field name to human-readable
const formatFieldName = (field) => {
    const fieldMappings = {
        'name': 'Name',
        'surname': 'Last Name',
        'street': 'Street',
        'city': 'City',
        'state': 'State',
        'zipcode': 'Zip Code',
        'phone': 'Phone',
        'cellphone': 'Cell Phone',
        'email': 'Email',
        'birthdayMonth': 'Birthday Month',
        'birthdayDay': 'Birthday Day',
        'birthdayYear': 'Birthday Year',
        'anniversaryMonth': 'Anniversary Month',
        'anniversaryDay': 'Anniversary Day',
        'anniversaryYear': 'Anniversary Year',
        'photo': 'Photo',
        'familyId': 'Family',
    };
    return fieldMappings[field] || field.charAt(0).toUpperCase() + field.slice(1);
};

// Format a single value for display
const formatValue = (value, fieldName) => {
    if (value === null || value === undefined || value === '') {
        return '(empty)';
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    // Special handling for photo field - show only filename
    if (fieldName === 'photo' && typeof value === 'string') {
        const parts = value.split('/');
        return parts[parts.length - 1] || value;
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    const str = String(value);
    return str.length > 40 ? str.substring(0, 37) + '...' : str;
};

// Extract meaningful changes from before/after objects
const extractChanges = (changes) => {
    if (!changes || typeof changes === 'string') {
        return null;
    }

    const { before, after } = changes;
    if (!before && !after) return null;

    const changedFields = [];
    // Fields to ignore in change display (internal/system fields)
    const ignoreFields = [
        'imageUpdatedAt', 'updatedAt', 'createdAt', 'orgId', 'id',
        'LastOriginalPhotoPathProcessed', 'MemberFacePaths', 'FacesExtractedAt',
        'lastOriginalPhotoPathProcessed', 'memberFacePaths', 'facesExtractedAt'
    ];

    const allKeys = new Set([
        ...(before ? Object.keys(before) : []),
        ...(after ? Object.keys(after) : [])
    ]);

    for (const key of allKeys) {
        if (ignoreFields.includes(key)) continue;
        const beforeVal = before?.[key];
        const afterVal = after?.[key];
        if (JSON.stringify(beforeVal) !== JSON.stringify(afterVal)) {
            changedFields.push({ field: key, from: beforeVal, to: afterVal });
        }
    }

    return changedFields.length > 0 ? changedFields : null;
};

// Action badge component
const ActionBadge = ({ action }) => {
    const config = {
        CREATE: { icon: faPlus, className: 'activity-action-create', label: 'Created' },
        UPDATE: { icon: faPencilAlt, className: 'activity-action-update', label: 'Updated' },
        DELETE: { icon: faTrashAlt, className: 'activity-action-delete', label: 'Deleted' },
        APPROVE: { icon: faUserCheck, className: 'activity-action-create', label: 'Approved' },
        REJECT: { icon: faBan, className: 'activity-action-delete', label: 'Rejected' },
        CLAIM: { icon: faUserPlus, className: 'activity-action-create', label: 'New User Account' }
    };
    const { icon, className, label } = config[action] || config.UPDATE;

    return (
        <span className={`activity-action-badge ${className}`}>
            <FontAwesomeIcon icon={icon} />
            {label}
        </span>
    );
};

// Entity type badge
const EntityBadge = ({ entityType }) => {
    const config = {
        FAMILY: { icon: faUsers, className: 'activity-entity-family', label: 'Family' },
        MEMBER: { icon: faUser, className: 'activity-entity-member', label: 'Member' },
        GROUP: { icon: faLayerGroup, className: 'activity-entity-group', label: 'Group' },
        EVENT: { icon: faCalendarAlt, className: 'activity-entity-event', label: 'Event' },
        EVENT_SERIES: { icon: faRepeat, className: 'activity-entity-event', label: 'Event Series' },
        JOIN_REQUEST: { icon: faUserCheck, className: 'activity-entity-join', label: 'Join Request' },
    };
    const { icon, className, label } = config[entityType] || config.MEMBER;

    return (
        <span className={`activity-entity-badge ${className}`}>
            <FontAwesomeIcon icon={icon} />
            {label}
        </span>
    );
};

function ActivityLogPage() {
    const { currentUser, isAdmin, userOrgId, loadingAuth } = useAuth();

    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [filterEntityType, setFilterEntityType] = useState('ALL');
    const [filterAction, setFilterAction] = useState('ALL');
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [allDocs, setAllDocs] = useState([]);

    // Entity names lookup cache
    const [entityNames, setEntityNames] = useState({});

    // Fetch activities
    const fetchActivities = useCallback(async (isLoadMore = false) => {
        if (!userOrgId) return;

        setLoading(true);
        setError(null);

        try {
            let q = query(
                collection(db, 'auditLog'),
                where('orgId', '==', userOrgId),
                orderBy('timestamp', 'desc'),
                limit(PAGE_SIZE * 5) // Fetch more to allow client-side filtering
            );

            if (isLoadMore && lastDoc) {
                q = query(
                    collection(db, 'auditLog'),
                    where('orgId', '==', userOrgId),
                    orderBy('timestamp', 'desc'),
                    startAfter(lastDoc),
                    limit(PAGE_SIZE * 5)
                );
            }

            const snapshot = await getDocs(q);
            const docs = snapshot.docs;

            if (!isLoadMore) {
                setAllDocs(docs);
            } else {
                setAllDocs(prev => [...prev, ...docs]);
            }

            setHasMore(docs.length === PAGE_SIZE * 5);
            if (docs.length > 0) {
                setLastDoc(docs[docs.length - 1]);
            }
        } catch (err) {
            console.error('Error fetching activity log:', err);
            if (err.code === 'failed-precondition' && err.message?.includes('index')) {
                setError('Activity log index is being created. Please try again in a few minutes.');
            } else {
                setError('Failed to load activity log.');
            }
        } finally {
            setLoading(false);
        }
    }, [userOrgId, lastDoc]);

    // Initial fetch
    useEffect(() => {
        if (!loadingAuth && userOrgId && isAdmin) {
            fetchActivities();
        }
    }, [loadingAuth, userOrgId, isAdmin]);

    // Extract a human-readable name from audit metadata for entity types that don't need DB lookups
    const getNameFromMetadata = (entityType, metadata) => {
        if (!metadata) return null;
        switch (entityType) {
            case 'EVENT':
            case 'EVENT_SERIES':
                return metadata.title || null;
            case 'GROUP':
                return metadata.groupName || metadata.title || null;
            case 'JOIN_REQUEST':
                return metadata.surname ? `${metadata.surname} Family` : null;
            default:
                return null;
        }
    };

    // Fetch entity names for display (batch lookup, skip already cached)
    useEffect(() => {
        if (allDocs.length === 0) return;

        const fetchEntityNames = async () => {
            // Collect unique entity IDs that we haven't fetched yet
            const familyIds = new Set();
            const memberIds = new Set();
            const newNames = { ...entityNames };

            allDocs.forEach(docSnap => {
                const data = docSnap.data();
                const key = `${data.entityType}:${data.entityId}`;

                // Skip if already cached or if it's a DELETE (data is gone)
                if (entityNames[key] || data.action === 'DELETE') return;

                // Resolve names from metadata for entity types that don't need DB lookups
                const metaName = getNameFromMetadata(data.entityType, data.metadata);
                if (metaName) {
                    newNames[key] = metaName;
                    return;
                }

                if (data.entityType === 'FAMILY') {
                    familyIds.add(data.entityId);
                } else if (data.entityType === 'MEMBER') {
                    memberIds.add(data.entityId);
                }
            });

            // Skip if nothing to fetch
            if (familyIds.size === 0 && memberIds.size === 0) {
                // Still update if we resolved event/group/series names from metadata
                if (Object.keys(newNames).length > Object.keys(entityNames).length) {
                    setEntityNames(newNames);
                }
                return;
            }

            // Fetch families
            const familyPromises = Array.from(familyIds).map(async (id) => {
                try {
                    const docSnap = await getDoc(doc(db, 'family', id));
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        newNames[`FAMILY:${id}`] = data.surname ? `${data.surname} Family` : null;
                    }
                } catch (err) {
                    console.warn(`Could not fetch family ${id}:`, err);
                }
            });

            // Fetch members
            const memberPromises = Array.from(memberIds).map(async (id) => {
                try {
                    const docSnap = await getDoc(doc(db, 'member', id));
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        newNames[`MEMBER:${id}`] = data.name ? `${data.name}${data.surname ? ' ' + data.surname : ''}` : null;
                    }
                } catch (err) {
                    console.warn(`Could not fetch member ${id}:`, err);
                }
            });

            await Promise.all([...familyPromises, ...memberPromises]);
            setEntityNames(newNames);
        };

        fetchEntityNames();
    }, [allDocs]);

    // Helper to get display name for an entity
    const getEntityDisplayName = (entityType, entityId, action, metadata) => {
        // For deletes, try to use metadata if available
        if (action === 'DELETE') {
            if (entityType === 'FAMILY' && metadata?.surname) {
                return `${metadata.surname} Family (deleted)`;
            }
            if (entityType === 'MEMBER' && metadata?.name) {
                return `${metadata.name}${metadata.surname ? ' ' + metadata.surname : ''} (deleted)`;
            }
            const metaName = getNameFromMetadata(entityType, metadata);
            if (metaName) return `${metaName} (deleted)`;
            return null; // Fall back to ID
        }

        // For non-delete actions, try metadata first, then cached lookup
        const metaName = getNameFromMetadata(entityType, metadata);
        if (metaName) return metaName;

        const key = `${entityType}:${entityId}`;
        return entityNames[key] || null;
    };

    // Apply filters to all docs
    const filteredActivities = allDocs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(activity => {
            // Hide join token entries
            if (activity.entityType === 'JOIN_TOKEN') return false;

            // Entity type filter
            if (filterEntityType !== 'ALL' && activity.entityType !== filterEntityType) {
                return false;
            }
            // Action filter
            if (filterAction !== 'ALL' && activity.action !== filterAction) {
                return false;
            }
            // Search filter (by email, entity ID, entity name, or changes)
            if (searchTerm) {
                const search = searchTerm.toLowerCase();
                const matchesEmail = activity.userEmail?.toLowerCase().includes(search);
                const matchesEntityId = activity.entityId?.toLowerCase().includes(search);
                const matchesChanges = JSON.stringify(activity.changes || {}).toLowerCase().includes(search);
                // Also search by entity name
                const entityName = entityNames[`${activity.entityType}:${activity.entityId}`];
                const matchesEntityName = entityName?.toLowerCase().includes(search);
                // Search metadata name/surname for deleted entries
                const matchesMetadataName = activity.metadata?.name?.toLowerCase().includes(search) ||
                    activity.metadata?.surname?.toLowerCase().includes(search);
                if (!matchesEmail && !matchesEntityId && !matchesChanges && !matchesEntityName && !matchesMetadataName) {
                    return false;
                }
            }
            return true;
        });

    // Paginate filtered results
    const paginatedActivities = filteredActivities.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const totalPages = Math.ceil(filteredActivities.length / PAGE_SIZE);

    // Reset page when filters change
    useEffect(() => {
        setPage(1);
    }, [filterEntityType, filterAction, searchTerm]);

    // Clear filters
    const clearFilters = () => {
        setFilterEntityType('ALL');
        setFilterAction('ALL');
        setSearchTerm('');
        setPage(1);
    };

    const hasActiveFilters = filterEntityType !== 'ALL' || filterAction !== 'ALL' || searchTerm !== '';

    // Not admin - redirect
    if (!loadingAuth && (!currentUser || !isAdmin)) {
        return <Navigate to="/dashboard" replace />;
    }

    // Loading state
    if (loadingAuth || (loading && allDocs.length === 0)) {
        return (
            <div className="activity-log-page">
                <div className="activity-log-loading">
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    <p>Loading activity log...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="activity-log-page">
            {/* Page Header */}
            <div className="activity-log-header">
                <div className="container">
                    <div className="activity-log-header-content">
                        <div className="activity-log-header-left">
                            <div className="activity-log-header-icon">
                                <FontAwesomeIcon icon={faHistory} />
                            </div>
                            <div>
                                <h1 className="activity-log-title">Activity Log <span className="admin-only-badge">Admin Only</span></h1>
                                <p className="activity-log-subtitle">
                                    {filteredActivities.length} {filteredActivities.length === 1 ? 'entry' : 'entries'}
                                    {hasActiveFilters && ' (filtered)'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                {/* Filters */}
                <div className="activity-log-filters">
                    <div className="activity-filter-group">
                        <label className="activity-filter-label">
                            <FontAwesomeIcon icon={faFilter} />
                            Type
                        </label>
                        <select
                            className="activity-filter-select"
                            value={filterEntityType}
                            onChange={(e) => setFilterEntityType(e.target.value)}
                        >
                            <option value="ALL">All Types</option>
                            <option value="FAMILY">Families</option>
                            <option value="MEMBER">Members</option>
                            <option value="GROUP">Groups</option>
                            <option value="EVENT">Events</option>
                            <option value="EVENT_SERIES">Event Series</option>
                        </select>
                    </div>

                    <div className="activity-filter-group">
                        <label className="activity-filter-label">Action</label>
                        <select
                            className="activity-filter-select"
                            value={filterAction}
                            onChange={(e) => setFilterAction(e.target.value)}
                        >
                            <option value="ALL">All Actions</option>
                            <option value="CREATE">Created</option>
                            <option value="UPDATE">Updated</option>
                            <option value="DELETE">Deleted</option>
                            <option value="APPROVE">Approved</option>
                            <option value="REJECT">Rejected</option>
                            <option value="CLAIM">New User Account</option>
                        </select>
                    </div>

                    <div className="activity-filter-group activity-filter-search">
                        <label className="activity-filter-label">
                            <FontAwesomeIcon icon={faSearch} />
                            Search
                        </label>
                        <div className="activity-search-input-wrapper">
                            <input
                                type="text"
                                className="activity-filter-input"
                                placeholder="Search by email, ID, or content..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button
                                    className="activity-search-clear"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>

                    {hasActiveFilters && (
                        <button className="activity-clear-filters" onClick={clearFilters}>
                            <FontAwesomeIcon icon={faTimes} />
                            Clear Filters
                        </button>
                    )}
                </div>

                {/* Error */}
                {error && (
                    <div className="activity-log-error">
                        {error}
                    </div>
                )}

                {/* Activity List */}
                <div className="activity-log-list">
                    {paginatedActivities.length === 0 ? (
                        <div className="activity-log-empty">
                            <FontAwesomeIcon icon={faHistory} className="activity-log-empty-icon" />
                            <h3>No Activity Found</h3>
                            <p>
                                {hasActiveFilters
                                    ? 'No entries match your current filters.'
                                    : 'No activity has been recorded yet.'}
                            </p>
                            {hasActiveFilters && (
                                <button className="activity-clear-filters-btn" onClick={clearFilters}>
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    ) : (
                        paginatedActivities.map((activity) => {
                            const changes = extractChanges(activity.changes);

                            return (
                                <div key={activity.id} className="activity-log-item">
                                    <div className="activity-item-header">
                                        <div className="activity-item-badges">
                                            <ActionBadge action={activity.action} />
                                            <EntityBadge entityType={activity.entityType} />
                                        </div>
                                        <span className="activity-item-time">
                                            {formatTimestamp(activity.timestamp)}
                                        </span>
                                    </div>

                                    <div className="activity-item-body">
                                        {/* Entity name - prominently displayed */}
                                        {(() => {
                                            const displayName = getEntityDisplayName(
                                                activity.entityType,
                                                activity.entityId,
                                                activity.action,
                                                activity.metadata
                                            );
                                            return displayName ? (
                                                <div className="activity-item-entity-name">
                                                    {displayName}
                                                </div>
                                            ) : (
                                                <div className="activity-item-entity-id">
                                                    <code>{activity.entityId}</code>
                                                </div>
                                            );
                                        })()}

                                        <div className="activity-item-user">
                                            by {activity.userEmail || 'Unknown'}
                                        </div>

                                        {changes && changes.length > 0 && (
                                            <div className="activity-item-changes">
                                                <strong>Changes:</strong>
                                                <div className="activity-changes-list">
                                                    {changes.map((change, i) => (
                                                        <div key={i} className="activity-change">
                                                            <span className="activity-change-field">
                                                                {formatFieldName(change.field)}:
                                                            </span>
                                                            {change.from !== undefined && (
                                                                <>
                                                                    <span className="activity-change-from">
                                                                        {formatValue(change.from, change.field)}
                                                                    </span>
                                                                    <span className="activity-change-arrow">→</span>
                                                                </>
                                                            )}
                                                            <span className="activity-change-to">
                                                                {formatValue(change.to, change.field)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {activity.action === 'DELETE' && (
                                            <div className="activity-item-deleted-note">
                                                Record data has been redacted for privacy.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="activity-log-pagination">
                        <button
                            className="activity-pagination-btn"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                            Previous
                        </button>

                        <span className="activity-pagination-info">
                            Page {page} of {totalPages}
                        </span>

                        <button
                            className="activity-pagination-btn"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        >
                            Next
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                )}

                {/* Load More (for fetching more from Firestore) */}
                {hasMore && filteredActivities.length >= allDocs.length * 0.8 && (
                    <div className="activity-log-load-more">
                        <button
                            className="activity-load-more-btn"
                            onClick={() => fetchActivities(true)}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    Loading...
                                </>
                            ) : (
                                'Load More History'
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ActivityLogPage;
